import type { Url } from 'next/dist/shared/lib/router/router';
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import { cn } from '@poseidon/components/cn';
import React from 'react';

import { Arrows, General } from '@poseidon/components/StrokeIcons';
import { Tooltip } from '@poseidon/components/Input2';

import NotificationLabel from '~/components/NotificationLabel';
import { ButtonLink, Button } from '@poseidon/components/Input2';

// Route-agnostic shape of a nav row (and its recursive sub-rows). The active
// pathname is NOT part of the data — it's injected via `RowProps` so rows never
// read the router themselves.
type RowData = LinkProps & {
  icon: React.JSX.Element;
  children?: React.ReactNode;
  className?: string;
  minimized?: boolean;
  counterValue?: number;
  subLinks?: RowData[];
  addLink?: Url;
  addLinkLabel?: string;
  newBadge?: boolean;
  gradientBadge?: boolean;
  gradientActive?: boolean;
  loading?: boolean;
  processingCount?: number;
};

type RowProps = RowData & {
  // Current route pathname, passed down so active state is computed from props
  // instead of `useRouter()`. Keeps the row (and `LeftNavPure`) renderable
  // without a router context.
  pathname: string;
  // Sublink expand/collapse is controlled by the container (hover when
  // minimized, chevron toggle otherwise), so the row holds no state of its own.
  // Omitted for rows without sublinks.
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Whether the notification badge should play its shake animation. Owned by the
  // container (see `useShakeOnChange` in LeftNav) so this stays a pure render.
  isShaking?: boolean;
};

export const LeftNavRow = ({
  href,
  icon,
  className,
  children,
  minimized,
  counterValue,
  subLinks,
  addLink,
  addLinkLabel,
  newBadge,
  gradientBadge,
  gradientActive,
  loading,
  pathname,
  isOpen,
  onOpenChange,
  isShaking,
  ...props
}: RowProps) => {
  const hrefString = typeof href === 'string' ? href : href?.pathname ?? '';

  const isActive = pathname === hrefString;
  const isPartialActive = pathname.startsWith(hrefString);

  // Auto-expand when the current route is one of this row's sublinks; the
  // controlled `isOpen` (from the container) covers hover + manual toggling.
  const open = (isOpen ?? false) || getSublinksUrls(subLinks).includes(pathname);

  const classList = cn(
    className,
    // Base row styles
    'flex flex-row relative font-normal text-base leading-6 gap-3 p-2 no-underline rounded-md h-10 items-center shrink-0 text-(--nav-txt-color) transition-all duration-200 overflow-hidden',
    // Hover state
    'hover:bg-(--nav-hover-bg-color)',
    // Active states
    (isActive || isPartialActive) && 'bg-(--nav-active-bg-color)',
    // Minimized gap
    minimized && 'gap-0',
  );

  const hasActions = !minimized && ((subLinks?.length ?? 0) > 0 || addLink != null);

  return (
    <div
      onMouseEnter={() => {
        if (minimized) {
          onOpenChange?.(true);
        }
      }}
      onMouseLeave={() => {
        if (minimized) {
          onOpenChange?.(false);
        }
      }}>
      <Tooltip tooltip={minimized ? children : undefined}>
        <div className="group relative w-full">
          <Link href={href} className={classList} {...props}>
            <span className={cn('size-6', gradientActive && 'text-indigo-600 dark:text-indigo-400')}>{icon}</span>
            <span
              className={cn(
                'whitespace-pre transition-opacity duration-200 ease-[cubic-bezier(0.05,0.61,0.41,0.95)]',
                minimized && 'opacity-0',
                gradientActive && 'font-bold text-indigo-600 dark:text-indigo-400',
              )}>
              {children}
            </span>
          </Link>
          {!loading && !!counterValue && (
            <NotificationLabel
              className={cn('pointer-events-none transition-all duration-200', minimized && '-right-5')}
              counterValue={counterValue}
              gradient={gradientBadge}
              isShaking={isShaking}
            />
          )}
          {hasActions && (
            <div className="absolute right-1 top-1/2 flex -translate-y-1/2 gap-1 opacity-90 transition-opacity group-hover:opacity-100">
              {addLink && (
                <ButtonLink
                  href={addLink}
                  size="sm"
                  text
                  tooltip={addLinkLabel}
                  onClick={() => onOpenChange?.(!(isOpen ?? false))}
                  icon={<General.PlusCircle />}
                />
              )}
              {(subLinks?.length ?? 0) > 0 && (
                <Button
                  text
                  size="sm"
                  onClick={() => onOpenChange?.(!(isOpen ?? false))}
                  icon={<Arrows.ChevronDown className={cn('size-5 transition-all', { 'rotate-180': open })} />}
                />
              )}
            </div>
          )}
          {newBadge && (
            <div
              className={cn(
                'pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 rounded-md px-1 py-0.5 text-2xs font-semibold uppercase',
                'bg-linear-to-r from-purple-600 to-zumthor-600 text-white',
                { hidden: minimized },
              )}>
              {' '}
              new{' '}
            </div>
          )}
        </div>
      </Tooltip>
      {(subLinks?.length ?? 0) > 0 && (
        <div
          className="overflow-hidden transition-[height] duration-200 ease-out"
          style={open ? { height: 'auto' } : { height: '0px' }}>
          <div className={cn('flex flex-col items-stretch gap-1 pb-1.5 pl-2.5 pt-2.5', minimized && 'pl-0')}>
            {subLinks?.map((el) => {
              const subKey = typeof el.href === 'string' ? el.href : (el.href?.pathname ?? '');
              return (
                <LeftNavRow
                  subLinks={el.subLinks}
                  href={el.href}
                  icon={el.icon}
                  minimized={minimized}
                  pathname={pathname}
                  key={subKey}>
                  {el.children}
                </LeftNavRow>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const getSublinksUrls = (subLinks?: RowData[]) => {
  if (!subLinks) return [];

  const sublinksUrls: string[] = [];
  const checkSublinks = (subLinks: RowData[]) => {
    subLinks.forEach((el) => {
      if (el.href && typeof el.href === 'string') sublinksUrls.push(el.href);
      if (el.subLinks) checkSublinks(el.subLinks);
    });
  };
  checkSublinks(subLinks);
  return sublinksUrls;
};
